import { Box, Flex, Text } from '@chakra-ui/react'
import { appColors } from '@theme/foundations/colors'
import { type ReactElement, useState } from 'react'

export type TagVariant =
  | 'info'
  | 'warning'
  | 'negative'
  | 'positive'
  | 'gray'
  | 'lightBlue'
  | 'lightGreen'
  | 'lightGray'
  | 'lightAmber'
  /** Light fill + border (e.g. KPI “Recommended” / Figma medal). */
  | 'softPositive'
  | 'softNegative'

type VariantStyle = {
  textColor: string
  background: string
  borderWidth?: string
  borderColor?: string
  borderRadius?: string
}

interface TagComponentProps {
  text: string
  icon?: ReactElement
  rightIcon?: ReactElement
  variant?: TagVariant
  color?: {
    textColor?: string
    background?: string
    hover?: string
    borderColor?: string
  }
  height?: string
  fitContent?: boolean
  fontSize?: string | number
  fontWeight?: string | number
  borderRadius?: string | number
  borderWidth?: string
}

const TagComponent: React.FC<TagComponentProps> = ({
  text,
  icon,
  variant,
  color,
  rightIcon,
  height,
  fitContent,
  fontSize,
  fontWeight,
  borderWidth,
  borderRadius,
}) => {
  const maxTextLength = 32
  const isTruncated = fitContent ? false : text?.length > maxTextLength

  const variantStyles: Record<TagVariant, VariantStyle> = {
    info: { textColor: 'white', background: 'info' },
    warning: { textColor: 'white', background: 'warning' },
    negative: { textColor: 'white', background: 'negative' },
    positive: { textColor: 'white', background: 'positive' },
    gray: { textColor: 'white', background: 'gray.700' },
    lightGray: { textColor: 'gray.600', background: 'muted' },
    lightBlue: { textColor: '#4B73B0', background: '#EFF3FF' },
    lightGreen: { textColor: 'green.700', background: 'green.100' },
    lightAmber: { textColor: appColors.amber['600'], background: appColors.amber['50'] },
    softPositive: {
      textColor: 'green.700',
      background: 'green.50',
      borderWidth: '1px',
      borderColor: 'green.200',
    },
    softNegative: {
      textColor: 'red.700',
      background: 'red.50',
      borderWidth: '1px',
      borderColor: 'red.200',
    },
  }

  // Get colors based on variant or use custom color prop
  const getColors = () => {
    if (color) return color
    if (variant) return variantStyles[variant]
    return variantStyles.gray
  }

  const colors = getColors() as VariantStyle & { hover?: string }

  const resolvedBorderWidth = borderWidth ?? colors.borderWidth
  const hasBorder = Boolean(resolvedBorderWidth && colors.borderColor)

  const [isOpen, setIsOpen] = useState(false)

  const handleClick = () => {
    if (!isTruncated) {
      return
    }
    setIsOpen(oldState => !oldState)
  }

  const handleMouseEnter = () => {
    if (!isTruncated) {
      return
    }
    setIsOpen(() => true)
  }

  const handleMouseLeave = () => {
    if (!isTruncated) {
      return
    }
    setIsOpen(() => false)
  }

  const props = {
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onClick: handleClick,
    color: colors.textColor,
  }

  return (
    <Box {...props}>
      <Flex
        justifyContent={'center'}
        alignItems={'center'}
        width={'fit-content'}
        borderRadius={borderRadius ?? '6px'}
        height={height ?? '26px'}
        color={colors.textColor}
        backgroundColor={colors.background}
        borderWidth={resolvedBorderWidth}
        borderStyle={hasBorder ? 'solid' : undefined}
        borderColor={colors.borderColor}
        style={{ padding: '4px 8px 4px 8px' }}
        zIndex={100}
        fontSize={fontSize ?? 14}
        fontWeight={fontWeight ?? 500}
      >
        <Flex minH={0} h='full' gap={1} alignItems='center'>
          {icon}
          <Text
            fontSize={fontSize ?? 14}
            fontWeight={fontWeight ?? 500}
            lineHeight='1'
            maxW={fitContent ? 'fit-content' : '280px'}
            isTruncated
            color={colors?.textColor ? colors?.textColor : ''}
          >
            {text}
          </Text>
          {rightIcon}
        </Flex>
      </Flex>
    </Box>
  )
}

export default TagComponent
